import type {
  RenewalPendingRequirement,
  RenewalSkippedSubfolder,
} from "../../api/renewals.ts";
import { requirementsMapping } from "../../components/folders/fixtures/requirementsMapping.tsx";
import { subfoldersMapping } from "../../components/folders/fixtures/subfoldersMapping.tsx";

/** Una línea del resumen de renovación: clave estable, texto y motivo crudo. */
interface RenewalSummaryLine {
  key: string;
  label: string;
  reason: string;
}

const humanize = (slug: string): string => slug.replace(/[-_]/g, " ");

/**
 * Los motivos del proyector, en castellano.
 *
 * El backend los manda en snake_case porque son identificadores estables, no
 * texto de pantalla. Un código que no está en la tabla cae a su versión
 * humanizada (guiones bajos a espacios) a propósito: si el proyector gana un
 * motivo nuevo y nadie actualiza esta tabla, el motivo sigue visible en vez de
 * desaparecer, porque un hueco se lee como "no falta nada".
 */
export const REASON_LABELS: Readonly<Record<string, string>> = {
  renta_y_vigencia_por_capturar:
    "la renta y la vigencia se capturan al revisar",
  sin_requisito_de_origen: "el expediente anterior no pedía este documento",
  origen_sin_entregar: "no se había entregado en el expediente anterior",
  origen_rechazado: "el documento del expediente anterior fue rechazado",
  archivo_no_disponible_en_almacenamiento:
    "el archivo del expediente anterior ya no está disponible",
  obligado_solidario_retirado_en_la_renovacion:
    "se retiró el obligado solidario al renovar",
  representante_sin_propietario_representado:
    "su propietario representado no llegó al expediente nuevo",
};

/** El motivo crudo de una línea del resumen, en castellano. */
export const reasonLabel = (reason: string): string =>
  REASON_LABELS[reason] ?? humanize(reason);

const participantLabel = (slug: string): string =>
  subfoldersMapping[slug] ?? humanize(slug);

const requirementLabel = (slug: string): string =>
  requirementsMapping[slug] ?? humanize(slug);

/**
 * Numera a los participantes que comparten rol.
 *
 * Con varios participantes por rol, dos inquilinos llevan el MISMO slug. El
 * número se asigna por PERSONA (`identity`), no por línea: así la misma persona
 * conserva su número en todas sus líneas y "(1)" nunca nombra a dos personas
 * distintas. Cuenta por orden de aparición entre quienes salen en el resumen;
 * no promete el `person_index` real, que no viaja.
 */
const participantSuffixes = (
  participants: Array<{ slug: string; identity: string }>,
): string[] => {
  const identitiesBySlug = new Map<string, string[]>();
  participants.forEach(({ slug, identity }) => {
    const identities = identitiesBySlug.get(slug) ?? [];
    if (!identities.includes(identity)) identities.push(identity);
    identitiesBySlug.set(slug, identities);
  });

  return participants.map(({ slug, identity }) => {
    const identities = identitiesBySlug.get(slug) ?? [];
    return identities.length > 1
      ? ` (${identities.indexOf(identity) + 1})`
      : "";
  });
};

/**
 * MB-379 — los requisitos pendientes del resumen, con nombre legible y una
 * clave única aunque los slugs se repitan (React avisa y mezcla nodos con
 * claves duplicadas).
 */
export const pendingRequirementLines = (
  pending: RenewalPendingRequirement[],
): RenewalSummaryLine[] => {
  const suffixes = participantSuffixes(
    pending.map((item) => ({
      slug: item.subfolderSlug,
      identity: item.subfolderId,
    })),
  );
  return pending.map((item, index) => ({
    key: `${index}:${item.subfolderSlug}/${item.requirementSlug}`,
    label: `${participantLabel(item.subfolderSlug)}${suffixes[index]}: ${requirementLabel(item.requirementSlug)}`,
    reason: item.reason,
  }));
};

/**
 * MB-379 — los participantes que no se trajeron, con la misma regla. Aquí cada
 * entrada ya es una persona distinta, así que su posición es su identidad.
 */
export const skippedSubfolderLines = (
  skipped: RenewalSkippedSubfolder[],
): RenewalSummaryLine[] => {
  const suffixes = participantSuffixes(
    skipped.map((item, index) => ({ slug: item.slug, identity: `${index}` })),
  );
  return skipped.map((item, index) => ({
    key: `${index}:${item.slug}`,
    label: `${participantLabel(item.slug)}${suffixes[index]}`,
    reason: item.reason,
  }));
};
